import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, LayoutDashboard, Users, FileText, CalendarCheck, CheckSquare, LogOut, TrendingUp, AlertCircle, FileBarChart, BookOpen, MessageCircle, Bell, Menu, X, Shield } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout, notificationVersion } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifRef = useRef(null);

  useEffect(() => {
    if (!token) {
      navigate('/auth');
      return;
    }
    fetchStats();
    fetchNotifications();
  }, [token]);

  // Refetch notifications & stats whenever a live event arrives via WebSocket.
  useEffect(() => {
    if (!token || notificationVersion === 0) return;
    fetchNotifications();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationVersion]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifs(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markAsRead = async (notifId) => {
    try {
      await axios.patch(`${API}/notifications/${notifId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const toggleNotifs = () => {
    setShowNotifs(!showNotifs);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Tableau de bord', path: '/dashboard' },
    { id: 'members', icon: Users, label: 'Bénéficiaires', path: '/dashboard/members' },
    { id: 'notes', icon: FileText, label: 'Notes de service', path: '/dashboard/notes' },
    { id: 'tasks', icon: CheckSquare, label: 'Tâches', path: '/dashboard/tasks' },
    { id: 'appointments', icon: CalendarCheck, label: 'Rendez-vous', path: '/dashboard/appointments' },
    { id: 'education', icon: BookOpen, label: 'Contenu éducatif', path: '/dashboard/education' },
    { id: 'messages', icon: MessageCircle, label: 'Messagerie', path: '/dashboard/messages', badge: stats?.unread_messages || 0 },
    { id: 'reports', icon: FileBarChart, label: 'Rapports', path: '/dashboard/reports' },
    ...(user?.is_admin ? [{ id: 'admin', icon: Shield, label: 'Administration', path: '/dashboard/admin' }] : []),
  ];

  const complianceData = stats ? [
    { name: 'Conforme', value: stats.compliance_score, color: '#81B29A' },
    { name: 'Non conforme', value: 100 - stats.compliance_score, color: '#F2CC8F' },
  ] : [];

  const handleNavClick = (item) => {
    setActiveView(item.id);
    navigate(item.path);
    setIsMobileMenuOpen(false);
  };

  const SidebarContent = () => (
    <>
      <nav className="flex-1 space-y-1 overflow-y-auto">
        {menuItems.map((item) => (
          <button
            key={item.id}
            data-testid={`nav-${item.id}`}
            onClick={() => handleNavClick(item)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors relative ${
              activeView === item.id
                ? 'bg-primary text-white'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium flex-1 text-left text-sm">{item.label}</span>
            {item.badge && item.badge > 0 && (
              <span className="px-2 py-0.5 bg-error text-white text-xs rounded-full min-w-[20px] text-center">
                {item.badge}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div className="border-t border-slate-100 pt-4 space-y-3">
        <div className="px-4">
          <p className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</p>
          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          <p className="text-xs text-secondary font-medium mt-1 truncate">{user?.organization}</p>
        </div>
        <Button
          data-testid="logout-button"
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="w-full"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Déconnexion
        </Button>
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <Heart className="w-12 h-12 text-primary fill-primary mx-auto mb-4 animate-pulse" />
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col lg:flex-row">
      {/* Mobile Header - only visible below lg */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Heart className="w-7 h-7 text-primary fill-primary" />
          <span className="text-xl font-extrabold font-heading text-primary-900">Carelio</span>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-xl hover:bg-slate-50 transition-colors"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6 text-slate-700" /> : <Menu className="w-6 h-6 text-slate-700" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay + Drawer */}
      {isMobileMenuOpen && (
        <>
          <div
            data-testid="mobile-menu-overlay"
            className="lg:hidden fixed inset-0 bg-black/40 z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <aside className="lg:hidden fixed top-0 left-0 h-full w-[280px] bg-white z-50 p-5 pt-4 flex flex-col shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Heart className="w-7 h-7 text-primary fill-primary" />
                <span className="text-xl font-extrabold font-heading text-primary-900">Carelio</span>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-1.5 rounded-lg hover:bg-slate-50"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <SidebarContent />
          </aside>
        </>
      )}

      {/* Desktop Sidebar - only visible on lg+ */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-slate-100 p-6 flex-col sticky top-0 h-screen flex-shrink-0">
        <div className="flex items-center gap-2 mb-8">
          <Heart className="w-8 h-8 text-primary fill-primary" />
          <span className="text-2xl font-extrabold font-heading text-primary-900">Carelio</span>
        </div>
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-6 lg:p-8 mt-[57px] lg:mt-0 min-w-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-6 md:mb-8 flex items-start sm:items-center justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-primary-900 font-heading mb-1 md:mb-2 truncate">
                Bienvenue, {user?.full_name}
              </h1>
              <p className="text-sm md:text-base text-slate-600">Voici un aperçu de votre activité</p>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
            <Card data-testid="stat-card-members">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-6 h-6 md:w-8 md:h-8 text-primary" />
                  <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-secondary" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-primary-900 mb-1">{stats?.total_members || 0}</h3>
                <p className="text-xs md:text-sm text-slate-600">Bénéficiaires</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-card-notes">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <FileText className="w-6 h-6 md:w-8 md:h-8 text-secondary" />
                  <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-secondary" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-primary-900 mb-1">{stats?.total_notes || 0}</h3>
                <p className="text-xs md:text-sm text-slate-600">Notes rédigées</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-card-tasks">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <CheckSquare className="w-6 h-6 md:w-8 md:h-8 text-accent" />
                  <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-error" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-primary-900 mb-1">{stats?.pending_tasks || 0}/{stats?.total_tasks || 0}</h3>
                <p className="text-xs md:text-sm text-slate-600">Tâches en attente</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-card-appointments">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <CalendarCheck className="w-6 h-6 md:w-8 md:h-8 text-info" />
                  <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-secondary" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-primary-900 mb-1">{stats?.upcoming_appointments || 0}</h3>
                <p className="text-xs md:text-sm text-slate-600">Rendez-vous à venir</p>
              </CardContent>
            </Card>
          </div>

          {/* Compliance Score */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Card data-testid="compliance-card">
              <CardHeader>
                <CardTitle className="text-base md:text-lg">Score de conformité</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={complianceData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={75}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {complianceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-center mt-3">
                  <p className="text-3xl md:text-4xl font-bold text-primary-900 mb-1">
                    {stats?.compliance_score.toFixed(1)}%
                  </p>
                  <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                    Basé sur le suivi de vos bénéficiaires : notes récentes, tâches complétées et rendez-vous planifiés.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="quick-actions-card">
              <CardHeader>
                <CardTitle className="text-base md:text-lg">Actions rapides</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-1 md:gap-3">
                  <button
                    data-testid="quick-action-dictate-report"
                    onClick={() => navigate('/dashboard/notes')}
                    className="flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-xl bg-secondary text-white border border-secondary hover:bg-secondary-hover hover:shadow-md transition-all text-left col-span-2 md:col-span-1"
                  >
                    <span className="text-base flex-shrink-0">🎤</span>
                    <span className="text-xs md:text-sm font-semibold">Dicter un rapport</span>
                  </button>
                  <button
                    data-testid="quick-action-add-member"
                    onClick={() => navigate('/dashboard/members')}
                    className="flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-xl border border-slate-200 text-slate-700 hover:border-primary hover:text-primary hover:bg-primary-50/50 transition-all text-left"
                  >
                    <Users className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-xs md:text-sm font-medium">Bénéficiaire</span>
                  </button>
                  <button
                    data-testid="quick-action-add-task"
                    onClick={() => navigate('/dashboard/tasks')}
                    className="flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-xl border border-slate-200 text-slate-700 hover:border-primary hover:text-primary hover:bg-primary-50/50 transition-all text-left"
                  >
                    <CheckSquare className="w-5 h-5 text-accent flex-shrink-0" />
                    <span className="text-xs md:text-sm font-medium">Tâche</span>
                  </button>
                  <button
                    data-testid="quick-action-schedule-appointment"
                    onClick={() => navigate('/dashboard/appointments')}
                    className="flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-xl border border-slate-200 text-slate-700 hover:border-primary hover:text-primary hover:bg-primary-50/50 transition-all text-left"
                  >
                    <CalendarCheck className="w-5 h-5 text-info flex-shrink-0" />
                    <span className="text-xs md:text-sm font-medium">Rendez-vous</span>
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Dashboard;
